/**
 * Mordeczka.eu Admin - Image Categories Page
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  Popconfirm,
  message,
  Card,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { imageCategoriesApi } from "../services/api";
import type { ImageCategory } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

function slugFromName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

export function ImageCategoriesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ImageCategory | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });
  const { canEdit, canDelete } = usePermissions();

  const { data: categoriesList, isLoading } = useQuery({
    queryKey: ["image-categories-public"],
    queryFn: () => imageCategoriesApi.listPublic(),
  });

  const createMutation = useMutation({
    mutationFn: imageCategoriesApi.create,
    onSuccess: () => {
      message.success("Kategorię utworzono pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["image-categories-public"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się utworzyć"),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; slug?: string; display_order?: number };
    }) => imageCategoriesApi.update(id, data),
    onSuccess: () => {
      message.success("Kategorię zaktualizowano pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["image-categories-public"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się zaktualizować"),
  });

  const deleteMutation = useMutation({
    mutationFn: imageCategoriesApi.delete,
    onSuccess: () => {
      message.success("Kategorię usunięto pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["image-categories-public"] });
    },
    onError: () => message.error("Nie udało się usunąć"),
  });

  const openModal = (item?: ImageCategory) => {
    setEditingItem(item || null);
    form.setFieldsValue({
      name: item?.name || "",
      slug: item?.slug || "",
      display_order: item?.display_order ?? 100,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (!editingItem && name) {
      form.setFieldsValue({ slug: slugFromName(name) });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
    } catch {
      // validation failed
    }
  };

  const list = categoriesList ?? [];
  const paginatedList = list.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize,
  );

  const columns: ColumnsType<ImageCategory> = [
    {
      title: "Kolejność",
      dataIndex: "display_order",
      key: "display_order",
      width: 100,
      sorter: (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
      defaultSortOrder: "ascend",
    },
    {
      title: "Nazwa",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      render: (text) => <code>{text}</code>,
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Usunąć tę kategorię?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            cancelText="Anuluj"
            okButtonProps={{ danger: true }}
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Kategorie obrazków
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
          disabled={!canEdit}
        >
          Dodaj kategorię
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={paginatedList}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: list.length,
            showSizeChanger: true,
            pageSizeOptions: ["50", "100", "500"],
            showTotal: (total) => `Łącznie ${total} kategorii`,
            onChange: (page, pageSize) =>
              setPagination({ current: page, pageSize: pageSize ?? 50 }),
          }}
        />
      </Card>

      <Modal
        title={editingItem ? "Edytuj kategorię" : "Dodaj kategorię"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okButtonProps={{
          disabled: !canEdit,
          title: !canEdit ? "Brak uprawnień do edycji" : undefined,
        }}
        okText="Zapisz"
        cancelText="Anuluj"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nazwa"
            rules={[{ required: true, message: "Wprowadź nazwę" }]}
          >
            <Input
              placeholder="np. Logosy platform streamingowych"
              onChange={handleNameChange}
            />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Slug (podkatalog)"
            rules={[
              { required: true, message: "Wprowadź slug" },
              {
                pattern: /^[a-z0-9_]+$/,
                message: "Tylko małe litery, cyfry i podkreślenia",
              },
            ]}
          >
            <Input placeholder="np. streaming_logos" />
          </Form.Item>
          <Form.Item
            name="display_order"
            label="Kolejność"
            rules={[{ required: true }]}
            initialValue={100}
          >
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ImageCategoriesPage;
